import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  X, 
  Calendar, 
  User, 
  MapPin, 
  BookOpen, 
  Layers, 
  Users as UsersIcon, 
  GraduationCap, 
  Eye, 
  EyeOff, 
  Play, 
  Coffee, 
  ChevronDown, 
  ChevronUp,
  SlidersHorizontal,
  Building2
} from 'lucide-react';

const FacultyTimetableModal = ({ timetable, onClose, departmentInfo }) => {
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedOverride, setSelectedOverride] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const { faculty, week, summary } = timetable;

  const daysOfWeek = useMemo(() => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'], []);

  const getDayEntries = useCallback((dayName) => {
    const day = week.find(d => d.dayName === dayName);
    return day ? day.entries : [];
  }, [week]);

  const getDayData = (dayName) => {
    return week.find(d => d.dayName === dayName);
  };

  const getDaySummary = (dayName) => {
    const day = getDayData(dayName);
    if (!day) return null;
    return {
      totalSessions: day.totalSessions || 0,
      teachingSessions: day.teachingSessions?.length || 0,
      labSessions: day.labSessions || 0,
      electiveSessions: day.electiveSessions || 0,
      theorySessions: day.theorySessions || 0,
      isToday: day.isToday || false,
      hasOverrides: day.hasOverrides || false
    };
  };

  const isSessionLive = useCallback((entry) => {
    if (!entry.timeSlot?.start || !entry.timeSlot?.end) return false;
    const now = new Date();
    const today = now.toDateString();
    const dayData = week.find(d => d.dayName === entry.dayOfWeek);
    if (!dayData || !dayData.isToday) return false;
    
    const start = new Date(`${today} ${entry.timeSlot.originalStart || entry.timeSlot.start}`);
    const end = new Date(`${today} ${entry.timeSlot.originalEnd || entry.timeSlot.end}`);
    return now >= start && now <= end;
  }, [week]);

  const getSessionEmoji = (entry) => {
    if (entry.isBreak) return '☕';
    if (entry.isCustomSession) return '⚡';
    if (entry.isLabSession || entry.isLab) return '🔬';
    if (entry.isElectiveSession) return '📚';
    if (entry.sessionType === 'theory') return '📖';
    return '📝';
  };

  const getSessionTypeLabel = (entry) => {
    if (entry.isBreak) return 'Break';
    if (entry.isCustomSession) return 'Custom';
    if (entry.isLabSession || entry.isLab) return 'Lab';
    if (entry.isElectiveSession) return 'Group';
    if (entry.sessionType === 'theory') return 'Theory';
    return entry.type || 'Session';
  };

  const getStatusColor = (entry) => {
    if (entry.isOverride) return 'bg-amber-50/70 border-amber-200';
    if (entry.isBreak) return 'bg-gray-50 border-gray-200';
    if (entry.isCustomSession) return 'bg-purple-50/70 border-purple-200';
    if (entry.isLabSession || entry.isLab) return 'bg-blue-50/70 border-blue-200';
    if (entry.isElectiveSession) return 'bg-teal-50/70 border-teal-200';
    return 'bg-white border-gray-200';
  };

  const getBadgeColor = (entry) => {
    if (entry.isOverride) return 'bg-amber-100 text-amber-700';
    if (entry.isBreak) return 'bg-gray-100 text-gray-600';
    if (entry.isCustomSession) return 'bg-purple-100 text-purple-700';
    if (entry.isLabSession || entry.isLab) return 'bg-blue-100 text-blue-700';
    if (entry.isElectiveSession) return 'bg-teal-100 text-teal-700';
    if (entry.sessionType === 'theory') return 'bg-emerald-100 text-emerald-700';
    return 'bg-gray-100 text-gray-600';
  };

  const getBorderColor = (entry) => {
    if (entry.isOverride) return 'border-l-4 border-l-amber-500';
    if (entry.isBreak) return 'border-l-4 border-l-gray-300';
    if (entry.isCustomSession) return 'border-l-4 border-l-purple-500';
    if (entry.isLabSession || entry.isLab) return 'border-l-4 border-l-blue-500';
    if (entry.isElectiveSession) return 'border-l-4 border-l-teal-500';
    return 'border-l-4 border-l-emerald-500';
  };

  const getFacultyName = (entry) => {
    if (entry.facultyDetails?.name) return entry.facultyDetails.name;
    if (entry.facultyName) return entry.facultyName;
    return null;
  };

  const formatTimeSlot = (timeSlot) => {
    if (!timeSlot) return '';
    return `${timeSlot.start || ''} - ${timeSlot.end || ''}`;
  };

  const hasClassInfo = (entry) => {
    return !!(entry.className);
  };

  const getGroupDisplayName = (entry) => {
    if (entry.isLanguageGroup) {
      return entry.electiveGroupName || 'Language';
    }
    return entry.electiveGroupName || 'Group';
  };

  const getFilteredGroupSubjects = (entry) => {
    if (!entry.groupDetails || !entry.groupDetails.subjects) return [];
    const currentFacultyId = entry.facultyId;
    return entry.groupDetails.subjects.map(subject => {
      const filteredSubGroups = subject.subGroups?.filter(sg => 
        sg.facultyId === currentFacultyId || sg.facultyName === entry.facultyName
      ) || [];
      return { ...subject, subGroups: filteredSubGroups };
    }).filter(subject => subject.subGroups.length > 0);
  };

  const toggleViewOriginal = (entryId) => {
    if (selectedOverride === entryId) {
      setSelectedOverride(null);
    } else {
      setSelectedOverride(entryId);
    }
  };

  const toggleGroupExpand = (entryId) => {
    setExpandedGroups(prev => ({ ...prev, [entryId]: !prev[entryId] }));
  };

  const firstDayWithEntries = useMemo(() => {
    for (const day of daysOfWeek) {
      const entries = getDayEntries(day);
      if (entries.length > 0) return day;
    }
    return null;
  }, [daysOfWeek, getDayEntries]);

  useEffect(() => {
    if (!selectedDay && firstDayWithEntries) {
      setSelectedDay(firstDayWithEntries);
    }
  }, [selectedDay, firstDayWithEntries]);

  const getUniqueClassNames = () => {
    const classNames = new Set();
    week.forEach(day => {
      day.entries.forEach(entry => {
        if (entry.className) classNames.add(entry.className);
      });
    });
    return Array.from(classNames);
  };

  const uniqueClassNames = getUniqueClassNames();
  const hasAnyOverrides = week.some(day => day.hasOverrides);
  const departmentName = departmentInfo?.name || 'Department';

  const getDayDotColor = (dayName) => {
    const dayData = getDayData(dayName);
    if (!dayData) return 'bg-gray-300';
    if (dayData.isToday) return 'bg-emerald-500';
    if (dayData.hasOverrides) return 'bg-amber-500';
    if (dayData.entries?.length > 0) return 'bg-green-500';
    return 'bg-gray-300';
  };

  const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const todayEntries = getDayEntries(todayName);
  const hasLiveSession = todayEntries.some(e => isSessionLive(e));

  return (
    <div className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-6xl h-[92vh] sm:h-[85vh] flex flex-col overflow-hidden">
        
        {/* ===== MODAL WINDOW HEADER ===== */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-emerald-100 rounded-xl hidden sm:block text-emerald-600">
              <User className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate flex items-center gap-1.5">
                  <Building2 className="w-4 h-4 text-emerald-600 hidden sm:inline" />
                  {faculty.name}'s Schedule
                </h2>
                {hasLiveSession && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full animate-pulse flex items-center gap-1">
                    <Play className="w-2 h-2 fill-current" /> Live
                  </span>
                )}
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
                  {departmentName}
                </span>
              </div>
              <p className="text-xs text-gray-500 truncate">{faculty.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-xl transition-colors text-gray-500"
            aria-label="Close schedule screen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ===== SUMMARY BRIEF STATISTICS BAND ===== */}
        <div className="overflow-x-auto bg-emerald-50/50 border-b border-gray-100 flex-shrink-0 hide-scrollbar">
          <div className="flex items-center gap-4 px-4 py-2 text-[11px] font-medium text-gray-600 whitespace-nowrap w-max sm:w-full sm:justify-between">
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-emerald-500" /> Total Slots: <strong className="text-gray-900">{summary.totalSessions || 0}</strong></span>
            <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5 text-emerald-500" /> Theory: <strong className="text-emerald-600">{summary.teachingSessions || 0}</strong></span>
            <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5 text-blue-500" /> Lab Classes: <strong className="text-blue-600">{summary.labSessions || 0}</strong></span>
            <span className="flex items-center gap-1"><UsersIcon className="w-3.5 h-3.5 text-teal-500" /> Electives: <strong className="text-teal-600">{summary.electiveSessions || 0}</strong></span>
            <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5 text-gray-500" /> Target Classes: <strong className="text-gray-900">{uniqueClassNames.length}</strong></span>
          </div>
        </div>

        {/* ===== CORE APPLICATION CONTENT GRID ===== */}
        <div className="flex-1 flex flex-col sm:flex-row overflow-hidden relative">
          
          {/* DESKTOP LAYOUT ONLY: LEFT SIDEBAR FOR DAYS SELECTOR */}
          <div className="hidden sm:block w-48 border-r border-gray-200 bg-gray-50/50 p-3 overflow-y-auto">
            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">Days of Week</span>
            <div className="space-y-1">
              {daysOfWeek.map((dayName) => {
                const entries = getDayEntries(dayName);
                const daySummary = getDaySummary(dayName);
                const isActive = selectedDay === dayName;
                const dotColor = getDayDotColor(dayName);
                
                return (
                  <button
                    key={dayName}
                    onClick={() => entries.length > 0 && setSelectedDay(dayName)}
                    disabled={entries.length === 0}
                    className={`w-full text-left px-3 py-2.5 rounded-xl text-xs font-semibold transition-all flex items-center justify-between ${
                      isActive ? 'bg-emerald-600 text-white shadow-sm' : entries.length > 0 ? 'hover:bg-gray-100 text-gray-700' : 'text-gray-300 cursor-not-allowed opacity-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-white' : dotColor}`}></span>
                      <span className="truncate">{dayName}</span>
                    </div>
                    {daySummary?.hasOverrides && <span className={isActive ? 'text-white' : 'text-amber-500'}>⚠️</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* MOBILE ONLY CAPABILITY: ACTIONS FLOAT BAR TO TRIGGER BOTTOM DRAWER */}
          <div className="sm:hidden bg-white border-b border-gray-200 p-3 flex justify-between items-center flex-shrink-0">
            <div>
              <span className="text-xs text-gray-400 block font-medium">Selected View Day</span>
              <span className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${getDayDotColor(selectedDay)}`}></span>
                {selectedDay || 'None chosen'}
              </span>
            </div>
            <button
              onClick={() => setIsMobileDrawerOpen(true)}
              className="px-3 py-1.5 bg-emerald-50 text-emerald-700 font-bold rounded-xl text-xs flex items-center gap-1.5 border border-emerald-100 active:scale-95 transition-transform"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Change Day
            </button>
          </div>

          {/* MAIN CONTAINER PANEL: POPULATES THE CHOSEN DAY'S TIMETABLE CARDS */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/40">
            {selectedDay && getDayEntries(selectedDay).length > 0 ? (
              getDayEntries(selectedDay).map((entry, idx) => {
                const facultyName = getFacultyName(entry);
                const typeLabel = getSessionTypeLabel(entry);
                const sessionEmoji = getSessionEmoji(entry);
                const statusColor = getStatusColor(entry);
                const badgeColor = getBadgeColor(entry);
                const borderColor = getBorderColor(entry);
                const groupDisplayName = getGroupDisplayName(entry);
                const isOriginalView = selectedOverride === entry._id;
                const isLive = isSessionLive(entry);
                const filteredGroupSubjects = entry.isElectiveSession && entry.groupDetails ? getFilteredGroupSubjects(entry) : [];
                const isGroupExpanded = expandedGroups[entry._id] || false;

                return (
                  <div
                    key={idx}
                    className={`p-4 rounded-xl border bg-white shadow-sm transition-all ${statusColor} ${borderColor} ${
                      isLive ? 'ring-2 ring-green-500 shadow-md' : ''
                    }`}
                  >
                    {/* Live Timing Tracker Info */}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap text-xs font-semibold">
                        <span>{sessionEmoji}</span>
                        <span className="font-mono text-gray-700 bg-gray-100/80 px-2 py-0.5 rounded-md">
                          {formatTimeSlot(entry.timeSlot)}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider ${badgeColor}`}>
                          {typeLabel}
                        </span>
                      </div>
                      
                      {isLive && (
                        <span className="text-[10px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full animate-pulse">
                          Active Now
                        </span>
                      )}
                    </div>

                    {/* Class Conditional Contents */}
                    {entry.isBreak ? (
                      <div className="mt-2 text-gray-600 font-bold text-sm flex items-center gap-2">
                        <Coffee className="w-4 h-4 text-gray-400" />
                        {entry.label || 'Recess / Rest Break'}
                      </div>
                    ) : (
                      <div className="mt-3 space-y-2">
                        <div>
                          <h4 className="font-bold text-gray-900 text-sm sm:text-base leading-snug">
                            {entry.subjectName}
                            {entry.subjectCode && <span className="text-xs text-gray-400 font-normal ml-1">({entry.subjectCode})</span>}
                          </h4>
                          {entry.isElectiveSession && (
                            <span className="inline-block mt-1 text-[10px] bg-teal-100/60 text-teal-800 font-medium px-2 py-0.5 rounded border border-teal-200">
                              {groupDisplayName}
                            </span>
                          )}
                        </div>

                        {/* Room Location Mapping Meta */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 pt-1 text-xs text-gray-600">
                          {hasClassInfo(entry) && (
                            <div className="flex items-center gap-1.5">
                              <GraduationCap className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                              <span className="font-medium truncate"><span className="text-gray-400">Class:</span> {entry.className}</span>
                            </div>
                          )}
                          {entry.room && (
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <span className="font-medium truncate"><span className="text-gray-400">Location:</span> {entry.room}</span>
                            </div>
                          )}
                          {facultyName && (
                            <div className="flex items-center gap-1.5">
                              <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <span className="font-medium truncate"><span className="text-gray-400">Instructor:</span> {facultyName}</span>
                            </div>
                          )}
                          {entry.batchName && entry.batchName !== 'Batch' && (
                            <div className="flex items-center gap-1.5">
                              <UsersIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <span className="font-medium truncate"><span className="text-gray-400">Batch Allocation:</span> {entry.batchName}</span>
                            </div>
                          )}
                        </div>

                        {/* Schedule Adjusted Swapper Trigger Controls */}
                        {entry.isOverride && (
                          <div className="pt-2 border-t border-dashed border-gray-200 flex items-center justify-between">
                            <span className="text-[10px] text-amber-700 font-medium">⚠️ Temporary modification today</span>
                            <button
                              onClick={() => toggleViewOriginal(entry._id)}
                              className="text-xs font-bold text-emerald-600 hover:text-emerald-800 flex items-center gap-1 active:scale-95 transition-transform"
                            >
                              {isOriginalView ? <><EyeOff className="w-3.5 h-3.5" /> Return View</> : <><Eye className="w-3.5 h-3.5" /> See Original</>}
                            </button>
                          </div>
                        )}

                        {/* Elective Inner Grid List Toggle View details */}
                        {entry.isElectiveSession && filteredGroupSubjects.length > 0 && (
                          <div className="pt-1">
                            <button
                              onClick={() => toggleGroupExpand(entry._id)}
                              className="flex items-center gap-1 text-xs text-emerald-600 font-bold mt-1"
                            >
                              <span>{isGroupExpanded ? 'Hide Division Groups' : 'View Student Subgroups'}</span>
                              {isGroupExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </button>
                            
                            {isGroupExpanded && (
                              <div className="mt-2 p-2.5 bg-gray-50 rounded-lg space-y-2 border border-gray-100 text-xs">
                                {filteredGroupSubjects.map((subject, sIdx) => (
                                  <div key={sIdx} className="space-y-1">
                                    <p className="font-bold text-gray-700">{subject.subjectName}</p>
                                    {subject.subGroups?.map((sg, sgIdx) => (
                                      <div key={sgIdx} className="pl-2 border-l border-emerald-200 text-gray-500 flex flex-col gap-0.5">
                                        <span className="font-medium text-gray-700">{sg.subGroupName || `Group ${sgIdx + 1}`}</span>
                                        {sg.facultyName && <span>Teacher: {sg.facultyName}</span>}
                                        {sg.studentIds && <span>Students: {sg.studentIds.length} enrolled</span>}
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400 bg-white rounded-xl border border-gray-100">
                <Calendar className="w-10 h-10 text-gray-300 mb-2" />
                <p className="text-sm font-semibold text-gray-700">No active classes listed</p>
                <p className="text-xs px-4">There are no periods or schedules mapped for this selected day frame.</p>
              </div>
            )}
          </div>

          {/* ===== MOBILE FIRST BOTTOM DRAW DRAWER INTERFACE APPROACH ===== */}
          {isMobileDrawerOpen && (
            <div className="sm:hidden fixed inset-0 z-[999999] bg-black/50 backdrop-blur-xs flex items-end">
              {/* Drawer Backdrop Overlay click dismiss handler */}
              <div className="absolute inset-0" onClick={() => setIsMobileDrawerOpen(false)} />
              
              {/* Drawer Shell Body */}
              <div className="relative w-full bg-white rounded-t-2xl shadow-2xl p-4 space-y-4 max-h-[60vh] overflow-y-auto z-10 animate-slide-up">
                <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-1" />
                <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                  <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Select Weekday View</h3>
                  <button 
                    onClick={() => setIsMobileDrawerOpen(false)} 
                    className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-500 font-bold text-xs"
                  >
                    Done
                  </button>
                </div>
                
                <div className="grid grid-cols-1 gap-2">
                  {daysOfWeek.map((dayName) => {
                    const entries = getDayEntries(dayName);
                    const daySummary = getDaySummary(dayName);
                    const isActive = selectedDay === dayName;
                    
                    return (
                      <button
                        key={dayName}
                        disabled={entries.length === 0}
                        onClick={() => {
                          setSelectedDay(dayName);
                          setIsMobileDrawerOpen(false);
                        }}
                        className={`w-full p-3 rounded-xl text-left text-sm font-bold flex items-center justify-between transition-colors ${
                          isActive 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                            : entries.length > 0 
                              ? 'bg-white text-gray-800 border border-gray-200 active:bg-gray-50' 
                              : 'bg-gray-50 text-gray-300 border border-gray-100 opacity-40 cursor-not-allowed'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${getDayDotColor(dayName)}`} />
                          {dayName}
                        </span>
                        <div className="flex items-center gap-2">
                          {daySummary?.hasOverrides && <span className="text-amber-500 text-xs">⚠️ Modified</span>}
                          <span className="text-xs font-normal text-gray-400">({entries.length} slots)</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* ===== FOOTER COLOR LABELS LEGEND STRIP ===== */}
        <div className="p-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-[10px] font-medium text-gray-500">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-white border border-gray-300 border-l-4 border-l-emerald-500" /> Theory</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-blue-50 border border-blue-200 border-l-4 border-l-blue-500" /> Lab</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-teal-50 border border-teal-200 border-l-4 border-l-teal-500" /> Elective</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-50 border border-amber-200 border-l-4 border-l-amber-500" /> Modified</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-gray-50 border border-gray-200 border-l-4 border-l-gray-300" /> Break</span>
            </div>
            {hasAnyOverrides && <div className="text-amber-600 font-semibold">⚠️ Contains modified schedules</div>}
          </div>
        </div>

      </div>
    </div>
  );
};

export default FacultyTimetableModal;